import React, { useEffect, useState } from "react";
import axios from "../../../axiosConfig";
import "./issuedBook.css";
import "../../../styles/explorePageCss/bookDisplay.css";
import PDFCoverPreview, { coverStreamUrl } from "../../../components/PDFCoverPreview";
import TagsFilter, {
  bookMatchesSelectedTags,
  tagsFromBooks,
} from "../../../components/TagsFilter";

const CHIP_TONES = ["primary", "mint", "sky", "lilac", "peach"];

const FILTER_DEFS = [
  { key: "subject", label: "Subject", optionsKey: "subjects", style: "checkbox" },
  { key: "grade", label: "Grade", optionsKey: "grades", style: "pills" },
  { key: "type", label: "Book Type", optionsKey: "types", style: "checkbox" },
  { key: "version", label: "Version", optionsKey: "versions", style: "checkbox" },
  { key: "country", label: "Country", optionsKey: "countries", style: "checkbox" },
  { key: "language", label: "Language", optionsKey: "languages", style: "checkbox" },
];

const INITIAL_FILTERS = {
  subject: [],
  grade: [],
  type: [],
  version: [],
  country: [],
  language: [],
  standard: [],
  tags: [],
};

const VERSION_FILTER_OPTIONS = ["Digital", "Print"];

const BOOKS_PER_PAGE = 20;
const GRADE_GROUP_ORDER = ["KG", "Grades", "Electives", "Other"];

function pageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const items = [1];
  if (current > 3) items.push("…");
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p += 1) {
    if (!items.includes(p)) items.push(p);
  }
  if (current < total - 2) items.push("…");
  if (!items.includes(total)) items.push(total);
  return items;
}

function gradeCategory(label) {
  const lower = String(label || "").toLowerCase();
  if (/\bkg\b|kindergarten|nursery|pre[-\s]?k/.test(lower)) return "KG";
  if (/elective/.test(lower)) return "Electives";
  if (/grade|year|class/.test(lower)) return "Grades";
  return "Other";
}

function gradeSortParts(label) {
  const text = String(label || "").trim();
  const group = GRADE_GROUP_ORDER.indexOf(gradeCategory(text));
  const numMatch = text.match(/(\d+)/);
  const num = numMatch ? parseInt(numMatch[1], 10) : 0;
  return [group < 0 ? 99 : group, num, text];
}

function sortGradeLabels(labels) {
  return [...labels].sort((a, b) => {
    const [ga, na, sa] = gradeSortParts(a);
    const [gb, nb, sb] = gradeSortParts(b);
    if (ga !== gb) return ga - gb;
    if (na !== nb) return na - nb;
    return String(sa).localeCompare(String(sb), undefined, { numeric: true, sensitivity: "base" });
  });
}

function groupGradeLabels(labels) {
  const sorted = sortGradeLabels(labels);
  return GRADE_GROUP_ORDER.map((name) => ({
    name,
    items: sorted.filter((label) => gradeCategory(label) === name),
  })).filter((group) => group.items.length > 0);
}

function parseDownloadFilename(contentDisposition, fallback) {
  if (!contentDisposition) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      /* keep looking */
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(contentDisposition);
  if (quoted?.[1]) return quoted[1];
  const plain = /filename=([^;]+)/i.exec(contentDisposition);
  if (plain?.[1]) return plain[1].trim();
  return fallback;
}

async function downloadOriginalFile(url, fallbackName) {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Please sign in again to download files.");
  }
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf, application/zip, application/octet-stream",
    },
    params: { token },
    responseType: "arraybuffer",
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    transformResponse: [(data) => data],
    validateStatus: (status) => status >= 200 && status < 300,
  });
  const contentType = String(res.headers["content-type"] || "");
  if (contentType.includes("application/json")) {
    let message = "Download failed";
    try {
      const text = new TextDecoder().decode(res.data);
      const parsed = JSON.parse(text);
      if (parsed?.error) message = parsed.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const blob = new Blob([res.data], {
    type: contentType.split(";")[0] || "application/octet-stream",
  });
  const filename = parseDownloadFilename(res.headers["content-disposition"], fallbackName);
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

// --- BookDetail Component (Grouped) ---
function BookDetail({ group, onGoBack }) {
  const main = group.digital || group.print;
  const digital = group.digital;
  const print = group.print; 
  // Iframe state for reading book
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [iframeBlobUrl, setIframeBlobUrl] = useState(null);
  const [readingBook, setReadingBook] = useState(false);
  const [viewerFullscreen, setViewerFullscreen] = useState(false);
  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const viewerTitle = group.title || "Book Reader";

  const openReader = (url, titleSuffix) => {
    setViewerFullscreen(false);
    setIframeUrl(url);
    setShowIframe(true);
    setViewMenuOpen(false);
    if (titleSuffix) {
      /* title shown from group; suffix applied in openBookVersion */
    }
  };

  const openBookVersion = async (book, label) => {
    if (!book?.book_id) {
      alert(`No ${label} version is available for this book.`);
      return;
    }

    const url = book.url || group.url;
    if (url && String(url).trim()) {
      openReader(String(url).trim());
      return;
    }

    try {
      setReadingBook(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`/books/${book.book_id}/stream-version`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const contentType = response.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        throw new Error("Book file not available");
      }
      if (iframeBlobUrl) URL.revokeObjectURL(iframeBlobUrl);
      const blob = new Blob([response.data], { type: contentType || "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      setIframeBlobUrl(objectUrl);
      openReader(objectUrl);
    } catch (err) {
      alert(`Unable to open the ${label.toLowerCase()} version. No uploaded PDF or URL is available.`);
    } finally {
      setReadingBook(false);
    }
  };

  // Handle Read Book - ask for format when both digital and print exist
  const handleReadBook = async () => {
    if (digital && print) {
      setViewMenuOpen(true);
      return;
    }
    if (digital) {
      await openBookVersion(digital, "Digital");
      return;
    }
    if (print) {
      await openBookVersion(print, "Print");
      return;
    }
    alert('This book does not have a URL or PDF configured. Please contact the administrator.');
  };

  // Close iframe
  const handleCloseIframe = () => {
    setShowIframe(false);
    setIframeUrl(null);
    setViewerFullscreen(false);
    if (iframeBlobUrl) {
      URL.revokeObjectURL(iframeBlobUrl);
      setIframeBlobUrl(null);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== "Escape") return;
      if (viewMenuOpen) {
        setViewMenuOpen(false);
        return;
      }
      if (pdfMenuOpen) {
        setPdfMenuOpen(false);
        return;
      }
      if (showIframe) {
        if (viewerFullscreen) {
          setViewerFullscreen(false);
          return;
        }
        handleCloseIframe();
        return;
      }
      onGoBack();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showIframe, viewerFullscreen, iframeBlobUrl, pdfMenuOpen, viewMenuOpen]);

  useEffect(() => {
    return () => {
      if (iframeBlobUrl) URL.revokeObjectURL(iframeBlobUrl);
    };
  }, [iframeBlobUrl]);

  const description = group.description || '';
  const words = description.split(/\s+/);
  const isLong = words.length > 50;
  const shortDesc = isLong ? words.slice(0, 50).join(' ') + '...' : description;

  const canUseDigital = !!(digital?.can_download);
  const canUsePrint = !!(print?.can_download);
  const hasUploaded = (book, flag) => !!(book && (book[flag] === true || book[flag] === 1 || book[flag] === "1"));
  const zipBook = (canUseDigital && (hasUploaded(digital, "has_zip") || digital?.zip_link) && digital)
    || (canUsePrint && (hasUploaded(print, "has_zip") || print?.zip_link) && print)
    || null;

  const downloadOptions = [
    {
      key: "digital_version",
      label: "Digital Version (PDF)",
      available: canUseDigital && hasUploaded(digital, "has_file"),
      run: () => handleDownloadVersion(digital.book_id, digital.version_label),
    },
    {
      key: "digital_cover",
      label: "Digital Cover (PDF)",
      available: canUseDigital && hasUploaded(digital, "has_cover"),
      run: () => handleDownloadCover(digital.book_id),
    },
    {
      key: "print_version",
      label: "Print Version (PDF)",
      available: canUsePrint && hasUploaded(print, "has_file"),
      run: () => handleDownloadVersion(print.book_id, print.version_label, "pdf"),
    },
    {
      key: "print_cover",
      label: "Print Cover (PDF)",
      available: canUsePrint && hasUploaded(print, "has_cover"),
      run: () => handleDownloadCover(print.book_id),
    },
    {
      key: "zip_file",
      label: "ZIP File",
      available: !!zipBook,
      run: () => handleDownloadZip(zipBook.book_id, zipBook.version_label),
    },
  ];

  const handleDownloadVersion = async (bookId, versionLabel) => {
    await downloadOriginalFile(
      `/books/${bookId}/download/${encodeURIComponent(versionLabel)}`,
      `Book_${bookId}_${versionLabel}.pdf`
    );
  };

  const handleDownloadCover = async (bookId) => {
    await downloadOriginalFile(
      `/books/${bookId}/download-cover`,
      `Book_${bookId}_cover.pdf`
    );
  };

  const handleDownloadZip = async (bookId, versionLabel) => {
    await downloadOriginalFile(
      `/books/${bookId}/download-zip/${encodeURIComponent(versionLabel)}`,
      `Book_${bookId}_${versionLabel}.zip`
    );
  };

  const runDownloadOption = async (option) => {
    if (!option?.available || downloadingKey) return;
    try {
      setDownloadingKey(option.key);
      await option.run();
    } catch (err) {
      alert("Download not permitted or this file is not available.");
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <>
      {showIframe && iframeUrl && (
        <div
          className={`explore-viewer-overlay ${viewerFullscreen ? "is-fullscreen" : ""}`}
          onClick={(e) => {
            if (e.target.classList.contains("explore-viewer-overlay")) handleCloseIframe();
          }}
          role="dialog"
          aria-modal="true"
          aria-label={viewerTitle}
        >
          <div className="explore-viewer-modal">
            <div className="explore-viewer-header">
              <h2 className="explore-viewer-title" title={viewerTitle}>
                {viewerTitle}
              </h2>
              <div className="explore-viewer-actions">
                <button
                  type="button"
                  className="explore-viewer-fullscreen"
                  onClick={() => setViewerFullscreen((v) => !v)}
                  aria-label={viewerFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  title={viewerFullscreen ? "Exit fullscreen" : "Fullscreen"}
                >
                  {viewerFullscreen ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                      <path d="M9 3H5v4M15 3h4v4M9 21H5v-4M15 21h4v-4" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                      <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  className="explore-viewer-close"
                  onClick={handleCloseIframe}
                  aria-label="Close reader"
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="explore-viewer-body">
              <iframe src={iframeUrl} className="explore-viewer-frame" title={viewerTitle} allowFullScreen />
            </div>
          </div>
        </div>
      )}

      {readingBook && (
        <div className="explore-viewer-overlay explore-iframe-loading">
          <div className="explore-viewer-loading-card">Opening book…</div>
        </div>
      )}

      {!showIframe && (
        <div
          className="bookx-details-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("bookx-details-overlay")) onGoBack();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="bookx-details-container">
            <div className="bookx-details-header">
              <h3 className="section-heading">Book Specifications</h3>
            </div>
            <button
              type="button"
              className="bookx-close-button"
              onClick={onGoBack}
              aria-label="Close details"
            >
              ×
            </button>

            <div className="bookx-details-content">
              <div className="bookx-table-cover-row">
                <div className="bookx-details-table-container">
                  <table className="bookx-details-table">
                    <tbody>
                      <tr>
                        <td><strong>Subject</strong></td>
                        <td>{group.subject_name || "N/A"}</td>
                      </tr>
                      <tr>
                        <td><strong>Grade</strong></td>
                        <td>{group.grade_level || "N/A"}</td>
                      </tr>
                      <tr>
                        <td><strong>Language</strong></td>
                        <td>{group.language_name || "N/A"}</td>
                      </tr>
                      <tr>
                        <td><strong>Type</strong></td>
                        <td>{group.book_type_title || "N/A"}</td>
                      </tr>
                      <tr>
                        <td><strong>Country</strong></td>
                        <td>{group.country_name || "N/A"}</td>
                      </tr>
                      <tr>
                        <td><strong>Digital ISBN</strong></td>
                        <td>
                          {group.digital_isbn ||
                            group.digital?.isbn_code ||
                            (group.digital ? group.isbn_code : null) ||
                            "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Print ISBN</strong></td>
                        <td>
                          {group.print_isbn ||
                            group.print?.isbn_code ||
                            (group.print && !group.digital ? group.isbn_code : null) ||
                            "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="bookx-details-image-wrapper">
                  {main?.book_id ? (
                    <PDFCoverPreview
                      pdfUrl={coverStreamUrl(
                        main.book_id,
                        group.last_updated_at || main.last_updated_at
                      )}
                      width={280}
                      height={360}
                      bookTitle={group.title}
                    />
                  ) : null}
                </div>
              </div>

              <div className="bookx-details-meta">
              <h2 className="bookx-details-title">{group.title}</h2>
              <div
                className="bookx-details-description"
                style={{ whiteSpace: "pre-line" }}
                dangerouslySetInnerHTML={{ __html: showFullDesc || !isLong ? description : shortDesc }}
              />
              {isLong && (
                <button
                  type="button"
                  className="issued-modal-readmore"
                  onClick={() => setShowFullDesc((v) => !v)}
                >
                  {showFullDesc ? "Read Less" : "Read More"}
                </button>
              )}

              <div className="bookx-details-tags">
                <strong>Tags: </strong>
                {group.tags?.length > 0
                  ? group.tags.map((tag) => (
                      <span key={tag.tag_id} className="chip chip-lilac">
                        {tag.tag_name}
                      </span>
                    ))
                  : "None"}
              </div>
              </div>
            </div>

              <div className="btn-row bookx-details-buttons issued-modal-actions">
                <button type="button" className="issued-modal-btn issued-modal-btn-secondary" onClick={onGoBack}>
                  Back
                </button>
                {(digital || print) && (
                  <button
                    type="button"
                    className="issued-modal-btn issued-modal-btn-primary"
                    onClick={handleReadBook}
                    disabled={readingBook}
                  >
                    {readingBook ? "Opening…" : "View Book"}
                  </button>
                )}
                {(digital?.can_download || print?.can_download) && (
                  <button
                    className="issued-modal-btn issued-modal-btn-secondary"
                    onClick={() => setPdfMenuOpen(true)}
                    type="button"
                  >
                    Download PDF
                  </button>
                )}
              </div>
            </div>
          </div>
      )}

      {pdfMenuOpen && (
        <div
          className="issued-download-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("issued-download-overlay")) setPdfMenuOpen(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Download files"
        >
          <div className="issued-download-popup">
            <div className="issued-download-popup-head">
              <h3>Download</h3>
              <button
                type="button"
                className="issued-download-popup-close"
                onClick={() => setPdfMenuOpen(false)}
                aria-label="Close download options"
              >
                ×
              </button>
            </div>
            <p className="issued-download-popup-copy">
              Available files can be downloaded. Options that were not uploaded are marked as not available.
            </p>
            <div className="issued-download-popup-actions">
              {downloadOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`issued-modal-btn ${
                    option.available ? "issued-modal-btn-primary" : "issued-modal-btn-unavailable"
                  }${option.key === "zip_file" ? " issued-download-option-zip" : ""}`}
                  disabled={!option.available || !!downloadingKey}
                  onClick={() => runDownloadOption(option)}
                >
                  <span className="issued-download-option-label">{option.label}</span>
                  <span className="issued-download-option-status">
                    {downloadingKey === option.key
                      ? "Downloading…"
                      : option.available
                        ? "Available"
                        : "Not available"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {viewMenuOpen && (
        <div
          className="issued-download-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("issued-download-overlay")) setViewMenuOpen(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Choose version to view"
        >
          <div className="issued-download-popup">
            <div className="issued-download-popup-head">
              <h3>View version</h3>
              <button
                type="button"
                className="issued-download-popup-close"
                onClick={() => setViewMenuOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="issued-download-popup-copy">
              Choose which format you want to open.
            </p>
            <div className="issued-download-popup-actions">
              <button
                type="button"
                className="issued-modal-btn issued-modal-btn-primary"
                disabled={!digital || readingBook}
                onClick={() => openBookVersion(digital, "Digital")}
              >
                Digital Version
              </button>
              <button
                type="button"
                className="issued-modal-btn issued-modal-btn-primary"
                disabled={!print || readingBook}
                onClick={() => openBookVersion(print, "Print")}
              >
                Print Version
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const Books = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOptions, setFilterOptions] = useState({
    subjects: [],
    grades: [],
    types: [],
    versions: VERSION_FILTER_OPTIONS,
    countries: [],
    languages: [],
    standards: [],
  });
  const [tagOptions, setTagOptions] = useState([]);

  const pinIssuedBooksScroll = () => {
    // Lower filter checkboxes can trigger browser scrollIntoView and
    // scroll the dual-pane layout off-screen on desktop. Keep panes pinned.
    const reset = () => {
      document
        .querySelectorAll(
          ".va-app .sa-content, .issued-books, .issued-books-layout, .issued-books-results"
        )
        .forEach((el) => {
          if (el instanceof HTMLElement) el.scrollTop = 0;
        });
    };
    reset();
    requestAnimationFrame(reset);
  };

  const toggleFilterValue = (key, value) => {
    setFilters((prev) => {
      const current = prev[key] || [];
      const exists = current.includes(value);
      return {
        ...prev,
        [key]: exists ? current.filter((v) => v !== value) : [...current, value],
      };
    });
    pinIssuedBooksScroll();
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setSearchTerm("");
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchGroupedBooks = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const { data } = await axios.get("/books/grouped", { headers });
        setGroups(data.books || []);
        const books = data.books || [];
        setFilterOptions({
          grades: sortGradeLabels([...new Set(books.map((g) => g.grade_level).filter(Boolean))]),
          languages: [...new Set(books.map((g) => g.language_name).filter(Boolean))],
          subjects: [...new Set(books.map((g) => g.subject_name).filter(Boolean))],
          types: [...new Set(books.map((g) => g.book_type_title).filter(Boolean))],
          versions: VERSION_FILTER_OPTIONS,
          countries: [...new Set(books.map((g) => g.country_name).filter(Boolean))],
          standards: [...new Set(books.map((g) => g.standard_name).filter(Boolean))],
        });
        setTagOptions(tagsFromBooks(books));
      } catch (err) {
        setGroups([]);
      }
    };
    fetchGroupedBooks();
  }, []);

  const filteredGroups = groups.filter((group) => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const searchMatch =
      !searchTerm ||
      (group.title?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.description?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.grade_level?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.subject_name?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.language_name?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.country_name?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.isbn_code?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.digital_isbn?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.print_isbn?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.digital?.isbn_code?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.print?.isbn_code?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      (group.standard_name?.toLowerCase() || "").includes(lowerCaseSearchTerm) ||
      group.tags?.some((tag) => tag.tag_name.toLowerCase().includes(lowerCaseSearchTerm));
    if (!searchMatch) return false;

    const match = (list, val) => list.length === 0 || list.includes(String(val));
    const matchVersion =
      !filters.version?.length ||
      filters.version.some(
        (v) => (v === "Digital" && group.digital) || (v === "Print" && group.print)
      );
    return (
      match(filters.subject, group.subject_name) &&
      match(filters.grade, group.grade_level) &&
      match(filters.type, group.book_type_title) &&
      matchVersion &&
      match(filters.country, group.country_name) &&
      match(filters.language, group.language_name) &&
      match(filters.standard, group.standard_name) &&
      bookMatchesSelectedTags(group, filters.tags)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / BOOKS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * BOOKS_PER_PAGE;
  const pagedGroups = filteredGroups.slice(pageStart, pageStart + BOOKS_PER_PAGE);
  const showingFrom = filteredGroups.length ? pageStart + 1 : 0;
  const showingTo = Math.min(pageStart + BOOKS_PER_PAGE, filteredGroups.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  return (
    <div className="app-container issued-books">
      <div className="page-head issued-books-head" style={{ marginBottom: 18 }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: 4 }}>
            Issued Books
          </h1>
          <p className="page-desc" style={{ margin: 0 }}>
            Browse the books assigned to your account.
          </p>
        </div>
        <div className="issued-books-count" aria-live="polite">
          <strong>{filteredGroups.length}</strong>
          <span>
            {filteredGroups.length === 1 ? "book" : "books"}
            {filteredGroups.length !== groups.length ? ` of ${groups.length}` : ""}
          </span>
        </div>
      </div>

      <div className="layout-with-filters issued-books-layout">
            <aside className="ib-filter-aside">
              <div className="filter-card ib-filter-panel">
                <div className="ib-filter-head">
                  <div className="ib-filter-title">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    <span>Filters</span>
                  </div>
                  <button type="button" className="ib-filter-clear" onClick={handleClearFilters}>
                    Clear all
                  </button>
                </div>

                <div className="ib-filter-body">
                  <section className="ib-filter-section">
                    <h4 className="ib-filter-section-title">
                      Tags
                      {filters.tags?.length ? ` (${filters.tags.length})` : ""}
                    </h4>
                    <TagsFilter
                      tagOptions={tagOptions}
                      selected={filters.tags || []}
                      onChange={(next) => setFilters((prev) => ({ ...prev, tags: next }))}
                    />
                  </section>

                  {FILTER_DEFS.map(({ key, label, optionsKey, style }) => {
                    const options = filterOptions[optionsKey] || [];
                    if (options.length === 0) return null;
                    const selected = filters[key] || [];

                    return (
                      <section key={key} className="ib-filter-section">
                        <h4 className="ib-filter-section-title">{label}</h4>

                        {style === "pills" ? (
                          key === "grade" ? (
                            <div className="ib-filter-grade">
                              {groupGradeLabels(options).map((group) => (
                                <div key={group.name} className="ib-filter-grade-group">
                                  <p className="ib-filter-grade-group-label">{group.name}</p>
                                  <div className="ib-filter-pills ib-filter-pills-grade">
                                    {group.items.map((opt) => {
                                      const active = selected.includes(opt);
                                      return (
                                        <button
                                          key={opt}
                                          type="button"
                                          className={`ib-filter-pill ${active ? "is-active" : ""}`}
                                          onClick={() => toggleFilterValue(key, opt)}
                                          title={opt}
                                        >
                                          {opt}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                          <div className="ib-filter-pills">
                            {options.map((opt) => {
                              const active = selected.includes(opt);
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  className={`ib-filter-pill ${active ? "is-active" : ""}`}
                                  onClick={() => toggleFilterValue(key, opt)}
                                  title={opt}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                          )
                        ) : (
                          <ul className="ib-filter-checks">
                            {options.map((opt) => {
                              const checked = selected.includes(opt);
                              return (
                                <li key={opt}>
                                  <label
                                    className={`ib-check ${checked ? "is-checked" : ""}`}
                                    onMouseDown={(e) => {
                                      // Avoid focus scrollIntoView scrolling the desktop pane away
                                      e.preventDefault();
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => toggleFilterValue(key, opt)}
                                    />
                                    <span className="ib-check-box" aria-hidden="true" />
                                    <span className="ib-check-label">{opt}</span>
                                  </label>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </section>
                    );
                  })}
                </div>
              </div>
            </aside>

            <div className="issued-books-results">
              <div className="issued-books-toolbar">
                <input
                  type="text"
                  placeholder="Search by title, description, or tag…"
                  className="text-input issued-books-search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <p className="issued-books-showing">
                  Showing {showingFrom}–{showingTo} of {filteredGroups.length} {filteredGroups.length === 1 ? "book" : "books"}
                </p>
              </div>

              <div className="explore-grid issued-books-grid">
                {pagedGroups.length === 0 ? (
                  <p className="no-books-message">No books match the selected filters.</p>
                ) : (
                  pagedGroups.map((group) => {
                    const main = group.digital || group.print;
                    const bookId = main?.book_id;
                    const uniqueKey = `${bookId}-${group.title}-${group.grade_level}-${group.version_label}`;
                    const title = group.title || "Untitled";
                    const gradeLabel = group.grade_level || null;
                    const pillChips = [
                      group.book_type_title,
                      group.subject_name,
                      group.country_name,
                      group.language_name,
                      group.version_label,
                    ].filter(Boolean);

                    return (
                      <article key={uniqueKey} className="explore-card">
                        <div className="explore-cover">
                          {bookId ? (
                            <PDFCoverPreview
                              pdfUrl={coverStreamUrl(
                                bookId,
                                group.last_updated_at ||
                                  group.digital?.last_updated_at ||
                                  group.print?.last_updated_at
                              )}
                              height={300}
                              bookTitle={title}
                            />
                          ) : null}
                          {(group.digital || group.print) && (
                            <div className="explore-format-badges">
                              {group.digital ? <span className="explore-format-badge">Digital</span> : null}
                              {group.print ? <span className="explore-format-badge">Print</span> : null}
                            </div>
                          )}
                        </div>
                        <div className="explore-body">
                          <h3 className="explore-title" title={title}>
                            {title}
                          </h3>
                          <div className="explore-tags">
                            {gradeLabel ? <span className="explore-meta-plain">{gradeLabel}</span> : null}
                            {pillChips.slice(0, 4).map((chip, i) => (
                              <span
                                key={`${chip}-${i}`}
                                className={`chip chip-${CHIP_TONES[i % CHIP_TONES.length]}`}
                              >
                                {chip}
                              </span>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="explore-view-btn"
                            onClick={() => setSelectedGroup(group)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              aria-hidden="true"
                            >
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                            View
                          </button>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>

              {filteredGroups.length > 0 && (
                <div className="pagination issued-books-pagination">
                  <span className="page-info">
                    Showing {showingFrom} to {showingTo} of {filteredGroups.length} books
                  </span>
                  {totalPages > 1 && (
                    <>
                      <button
                        type="button"
                        className="page-btn"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={safePage === 1}
                        aria-label="Previous page"
                      >
                        ‹
                      </button>
                      {pageItems(safePage, totalPages).map((item, idx) =>
                        item === "…" ? (
                          <button key={`e-${idx}`} type="button" className="page-btn" disabled>
                            …
                          </button>
                        ) : (
                          <button
                            key={item}
                            type="button"
                            className={`page-btn${item === safePage ? " active" : ""}`}
                            onClick={() => setCurrentPage(item)}
                          >
                            {item}
                          </button>
                        )
                      )}
                      <button
                        type="button"
                        className="page-btn"
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={safePage === totalPages}
                        aria-label="Next page"
                      >
                        ›
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

      {selectedGroup && (
        <BookDetail group={selectedGroup} onGoBack={() => setSelectedGroup(null)} />
      )}
    </div>
  );
};

export default Books;